import { useEffect, useRef } from 'react';

import { UseForwardedRefProps } from './use-forwarded-ref.types';

/**
 * Hook to safely merge a forwarded ref with an internal ref.
 * Ensures internal access to the element while properly setting the forwarded ref.
 *
 * @template T - A type that extends HTMLElement.
 * @param {UseForwardedRefProps<T>} options - The forwarded ref and optional initial value.
 * @returns {React.RefObject<T>} A ref object you can use internally.
 *
 * @example
 * const ref = useForwardedRef({ forwardedRef });
 * return <div ref={ref} />;
 */
export const useForwardedRef = <T extends HTMLElement = HTMLElement>({
  forwardedRef,
  initialValue = null,
}: UseForwardedRefProps<T>): React.RefObject<T> => {
  const internalRef = useRef<T>(initialValue);

  useEffect(() => {
    if (!forwardedRef) return;

    if (typeof forwardedRef === 'function') {
      forwardedRef(internalRef.current);
    } else if ('current' in forwardedRef) {
      try {
        // Safely assign only if it's a mutable ref
        (forwardedRef as React.MutableRefObject<T | null>).current = internalRef.current;
      } catch {
        // Read-only ref (RefObject) — ignore safely
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            'Attempted to assign to a read-only forwardedRef. Use a mutable ref if the consumer needs access.'
          );
        }
      }
    }
  }, [forwardedRef]);

  return internalRef;
};
